import React from "react";
import {useTranslation} from "react-i18next";
import {motion, AnimatePresence} from "framer-motion";
import {Flame, Check, X, Trophy} from "lucide-react";
import AnimatedCounter from "@/components/AnimatedCounter.jsx";

// Écran "Tirs au but" — mode plein écran immersif, réactif tir-par-tir.
//
// PURE PRÉSENTATION : aucune logique de séance ici (pas de feed, pas de scoring, pas de séquence).
// Entièrement piloté par ses props. En démo c'est DemoPage qui simule le "feed" ; en prod ce sera
// le SSE (PENALTY_KICK_OPEN / PENALTY_KICK_RESULT) qui alimentera EXACTEMENT les mêmes props.
//
// ⚠️ Pas de décompte/timer visuel : la fenêtre n'a pas de deadline connue côté joueur — elle reste
// ouverte jusqu'à l'arrivée de l'event du tir suivant (back). Une fois le prono envoyé, on attend
// simplement la frappe. Le scoring "vite + juste" se joue au rang d'arrivée, côté back.

const penFlagSrc = (team) => team?.code
    ? `https://flagcdn.com/w160/${team.code.toLowerCase()}.png`
    : team?.logoUrl;

const penIsFlag = (team) => Boolean(team?.code || team?.logoUrl?.startsWith('https://flagcdn.com/'));

// Drapeau / logo cohérent avec TeamDisplay : flag-box rectangulaire (object-fill) pour les sélections.
const PenFlag = ({team, className = "w-10 h-7"}) => {
    if (!team) return null;
    const flag = penIsFlag(team);
    return (
        <div className={`${flag ? `${className} rounded-md overflow-hidden flag-box ring-1 ring-white/15` : 'w-9 h-9'} shrink-0`}>
            <img
                src={penFlagSrc(team)}
                alt={team.shortName || team.name}
                className={flag ? "w-full h-full object-fill" : "w-full h-full object-contain"}
                onError={(e) => { e.target.src = 'https://via.placeholder.com/50?text=?'; }}
            />
        </div>
    );
};

// Pastilles ✓ / ✗ : 5 emplacements par équipe, remplis par l'historique des tirs.
const penDotsFor = (history, side) => {
    const results = history.filter(h => h.taker === side).map(h => h.result);
    return Array.from({length: Math.max(5, results.length)}, (_, i) => results[i] ?? null);
};

// Team kick (GOAL/MISS) AND per-player prediction (CORRECT/WRONG/ABSENT) share the same dots:
// green = good, red = bad, grey = none/not played.
const PenDot = ({result}) => (
    <span className={[
        "w-2.5 h-2.5 rounded-full",
        (result === 'GOAL' || result === 'CORRECT') ? "bg-action-green" :
            (result === 'MISS' || result === 'WRONG') ? "bg-red-500" :
                "bg-white/15"
    ].join(' ')}/>
);

// A row of prediction dots, capped so a long (sudden-death) shootout doesn't overflow the row:
// the most recent MAX kicks + a "+k" marker for the earlier ones.
const PEN_DOTS_MAX = 12;
const PenDots = ({kicks}) => {
    const extra = kicks.length - PEN_DOTS_MAX;
    const shown = extra > 0 ? kicks.slice(-PEN_DOTS_MAX) : kicks;
    return (
        <span className="flex items-center gap-1 shrink-0">
            {extra > 0 && <span className="text-[10px] text-white/40 font-bold mr-0.5">+{extra}</span>}
            {shown.map((r, i) => <PenDot key={i} result={r}/>)}
        </span>
    );
};

// Badge de rang aligné sur le RankBadge du classement (or / argent / bronze ; vert pour moi).
const PenRankBadge = ({rank, isMe}) => (
    <div className={[
        "shrink-0 w-7 h-7 rounded-full flex items-center justify-center text-xs font-black",
        isMe ? "bg-action-green text-[#0B0F19]"
            : rank === 1 ? "bg-yellow-400/90 text-yellow-950"
                : rank === 2 ? "bg-slate-300/90 text-slate-800"
                    : rank === 3 ? "bg-orange-400/90 text-orange-950"
                        : "bg-white/10 text-white/60"
    ].join(' ')}>
        {rank}
    </div>
);

const PenRow = ({row}) => (
    <motion.div layout
                transition={{type: "spring", stiffness: 500, damping: 40}}
                className={[
                    "flex items-center gap-2.5 rounded-xl pl-1.5 pr-3 py-1.5",
                    row.isMe ? "bg-action-green/15 ring-1 ring-action-green/40" : "bg-white/[0.05]"
                ].join(' ')}>
        <PenRankBadge rank={row.rank} isMe={row.isMe}/>
        <span className={`flex-1 truncate text-sm font-semibold ${row.isMe ? 'text-action-green' : 'text-white/85'}`}>
            {row.label}
        </span>
        {/* Per-kick prediction dots — same bullets as the teams up top (vert/rouge/gris). */}
        {row.penaltyKicks?.length > 0 && <PenDots kicks={row.penaltyKicks}/>}
        {row.rank === 1 && <Trophy size={14} className="text-yellow-400 shrink-0"/>}
        <AnimatedCounter
            value={row.points}
            enableEffect={row.isMe}
            className={`font-black tabular-nums ${row.isMe ? 'text-action-green' : 'text-white/90'}`}
            ptsClass="text-[10px]"
        />
    </motion.div>
);

export function PenaltyShootout({
    homeTeam, awayTeam,
    kickNumber, taker,
    phase, actualResult,
    myPrediction, onPredict,
    lastOutcome,
    score = {home: 0, away: 0},
    history = [],
    board = [],
    myStreak = 0,
}) {
    const {t} = useTranslation();

    // null before the very first kick (UNKNOWN): the shooter isn't known yet, show a neutral medallion.
    const takerTeam = taker === 'HOME' ? homeTeam : taker === 'AWAY' ? awayTeam : null;
    const isOpen = phase === 'OPEN';
    const locked = myPrediction != null;

    // Mini-classement : top 4, en garantissant que "moi" soit toujours visible.
    const meIdx = board.findIndex(p => p.isMe);
    const top = board.slice(0, 4).map((p, i) => ({...p, rank: i + 1}));
    const meDetached = meIdx >= 4 ? {...board[meIdx], rank: meIdx + 1} : null;
    const rowLabel = (p) => p.isMe ? t('penalty.you', 'Toi') : p.pseudo;

    return (
        <div className="h-[100dvh] flex flex-col bg-[#0B0F19] text-white relative overflow-hidden">
            {/* Médaillon tireur (ondes + 1er tireur + flag) : on réduit TOUT le bloc sur écran court
                (le scale touche aussi les enfants, contrairement à une simple taille de conteneur). */}
            <style>{`
                .pen-taker { transform-origin: center; }
                @media (max-height: 760px) { .pen-taker { transform: scale(0.86); } }
                @media (max-height: 680px) { .pen-taker { transform: scale(0.72); } }
                @media (max-height: 600px) { .pen-taker { transform: scale(0.6); } }
            `}</style>
            {/* Ambiance stade */}
            <div className="pointer-events-none absolute inset-0"
                 style={{background: "radial-gradient(130% 55% at 50% -10%, rgba(16,185,129,0.20), transparent 62%), radial-gradient(110% 50% at 50% 112%, rgba(99,102,241,0.16), transparent 60%)"}}/>

            {/* --- SCOREBOARD --- */}
            <div className="relative z-10 px-5 pt-7 shrink-0">
                <div className="text-center mb-4">
                    <span className="inline-flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-[0.2em] text-action-green">
                        <span className="w-1.5 h-1.5 rounded-full bg-action-green animate-pulse"/>
                        {t('penalty.live_badge', 'Tirs au but')}
                    </span>
                </div>
                <div className="flex items-stretch justify-between gap-3">
                    {/* HOME */}
                    <div className="flex-1 flex items-center gap-2.5">
                        <PenFlag team={homeTeam} className="w-11 h-8"/>
                        <div className="min-w-0">
                            <div className="text-sm font-extrabold truncate">{homeTeam.shortName}</div>
                            <div className="flex flex-wrap gap-1 mt-1.5">
                                {penDotsFor(history, 'HOME').map((r, i) => <PenDot key={i} result={r}/>)}
                            </div>
                        </div>
                    </div>
                    {/* SCORE */}
                    <div className="flex items-center gap-2 px-1 shrink-0">
                        <span className="text-3xl font-black tabular-nums">{score.home}</span>
                        <span className="text-xl font-black text-white/30">:</span>
                        <span className="text-3xl font-black tabular-nums">{score.away}</span>
                    </div>
                    {/* AWAY */}
                    <div className="flex-1 flex items-center gap-2.5 flex-row-reverse text-right">
                        <PenFlag team={awayTeam} className="w-11 h-8"/>
                        <div className="min-w-0">
                            <div className="text-sm font-extrabold truncate">{awayTeam.shortName}</div>
                            <div className="flex flex-wrap gap-1 mt-1.5 flex-row-reverse">
                                {penDotsFor(history, 'AWAY').map((r, i) => <PenDot key={i} result={r}/>)}
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* --- ZONE CENTRALE (seule à se comprimer ; clippe le médaillon décoratif sur écran court) --- */}
            <div className="relative z-10 flex-1 min-h-0 overflow-hidden flex flex-col items-center justify-center px-6 py-4">
                <AnimatePresence mode="wait">
                    {isOpen ? (
                        <motion.div key={`open-${kickNumber}`}
                                    initial={{opacity: 0, scale: 0.9}}
                                    animate={{opacity: 1, scale: 1}}
                                    exit={{opacity: 0, scale: 0.9}}
                                    className="flex items-center justify-center">
                            {/* Tireur mis en scène : ondes sonar + halo diffus, drapeau seul (épuré, grandiose) */}
                            <div className="relative flex items-center justify-center pen-taker" style={{width: 300, height: 300}}>
                                {[0, 1, 2].map(i => (
                                    <motion.span key={i}
                                                 className="absolute rounded-full border-2 border-action-green/30"
                                                 style={{width: 140, height: 140}}
                                                 animate={{scale: [1, 2.8], opacity: [0.6, 0]}}
                                                 transition={{duration: 2.6, repeat: Infinity, ease: "easeOut", delay: i * 0.85}}
                                    />
                                ))}
                                <motion.span
                                    className="absolute rounded-full bg-action-green/25 blur-3xl"
                                    style={{width: 220, height: 220}}
                                    animate={{scale: [1, 1.12, 1], opacity: [0.55, 0.3, 0.55]}}
                                    transition={{duration: 2, repeat: Infinity, ease: "easeInOut"}}
                                />
                                <motion.div
                                    className="relative drop-shadow-[0_10px_35px_rgba(16,185,129,0.5)] flex flex-col items-center gap-2"
                                    animate={{scale: [1, 1.05, 1]}}
                                    transition={{duration: 2, repeat: Infinity, ease: "easeInOut"}}>
                                    {takerTeam ? (
                                        <PenFlag team={takerTeam} className="w-36 h-24"/>
                                    ) : (
                                        <>
                                            <div className="w-36 h-24 flex items-center justify-center text-7xl">⚽</div>
                                            <span className="text-white/60 text-sm font-bold uppercase tracking-wide">
                                                {t('penalty.first_taker', 'Premier tireur')}
                                            </span>
                                        </>
                                    )}
                                </motion.div>
                            </div>
                        </motion.div>
                    ) : (
                        <motion.div key={`reveal-${kickNumber}`}
                                    initial={{opacity: 0, y: 14}}
                                    animate={{opacity: 1, y: 0}}
                                    exit={{opacity: 0, y: -14}}
                                    className="flex flex-col items-center">
                            <motion.div
                                initial={{scale: 0.4, rotate: -6}}
                                animate={{scale: 1, rotate: 0}}
                                transition={{type: "spring", stiffness: 260, damping: 14}}
                                className={[
                                    "w-28 h-28 rounded-[28px] flex items-center justify-center text-5xl shadow-2xl",
                                    actualResult === 'GOAL'
                                        ? "bg-action-green/15 ring-2 ring-action-green"
                                        : "bg-red-500/15 ring-2 ring-red-500"
                                ].join(' ')}>
                                {actualResult === 'GOAL' ? '🥅' : '🧤'}
                            </motion.div>
                            <div className={`mt-4 text-2xl font-black uppercase tracking-wide ${actualResult === 'GOAL' ? 'text-action-green' : 'text-red-400'}`}>
                                {actualResult === 'GOAL' ? t('penalty.goal', 'But !') : t('penalty.miss', 'Raté !')}
                            </div>

                            {lastOutcome && (
                                <motion.div initial={{opacity: 0, y: 6}} animate={{opacity: 1, y: 0}}
                                            transition={{delay: 0.15}} className="mt-4 h-7 flex items-center">
                                    {myPrediction == null ? (
                                        <span className="text-white/45 text-sm">{t('penalty.too_late', 'Pas de prono à temps')}</span>
                                    ) : lastOutcome.correct ? (
                                        <div className="flex items-center gap-2">
                                            <span className="text-action-green font-black text-2xl">+{lastOutcome.points}</span>
                                            {lastOutcome.streak >= 2 && (
                                                <span className="inline-flex items-center gap-1 text-orange-400 font-bold text-xs bg-orange-400/10 px-2 py-0.5 rounded-full">
                                                    <Flame size={13}/> x{lastOutcome.streak}
                                                </span>
                                            )}
                                        </div>
                                    ) : (
                                        <span className="text-red-400/80 text-sm">{t('penalty.wrong', 'Mauvais prono — série perdue')}</span>
                                    )}
                                </motion.div>
                            )}
                        </motion.div>
                    )}
                </AnimatePresence>
            </div>

            {/* --- MINI CLASSEMENT --- */}
            <div className="relative z-10 px-5 shrink-0">
                <div className="flex items-center justify-between mb-2">
                    <span className="text-[11px] uppercase tracking-[0.2em] text-white/40">
                        {t('penalty.board_title', 'Classement de la séance')}
                    </span>
                    {myStreak >= 2 && (
                        <span className="inline-flex items-center gap-1 text-orange-400 text-xs font-bold">
                            <Flame size={13}/> {t('penalty.streak', 'Série')} {myStreak}
                        </span>
                    )}
                </div>
                <div className="space-y-1.5">
                    {top.map(p => <PenRow key={p.id} row={{...p, label: rowLabel(p)}}/>)}
                    {meDetached && (
                        <>
                            <div className="text-center text-white/25 text-xs leading-none">···</div>
                            <PenRow row={{...meDetached, label: rowLabel(meDetached)}}/>
                        </>
                    )}
                </div>
            </div>

            {/* --- BOUTONS PRONO (thumb-zone, toujours visibles : jamais comprimés) --- */}
            <div className="relative z-10 px-5 pt-4 pb-7 shrink-0">
                <AnimatePresence mode="wait">
                    {isOpen && !locked ? (
                        <motion.div key="buttons"
                                    initial={{opacity: 0, y: 16}} animate={{opacity: 1, y: 0}} exit={{opacity: 0, y: 16}}
                                    className="grid grid-cols-2 gap-3">
                            <button onClick={() => onPredict('GOAL')}
                                    className="flex items-center justify-center gap-2 py-4 rounded-2xl bg-action-green text-[#0B0F19] font-black text-lg active:scale-95 transition shadow-lg shadow-action-green/30">
                                <Check size={24} strokeWidth={3}/> {t('penalty.bet_goal', 'BUT')}
                            </button>
                            <button onClick={() => onPredict('MISS')}
                                    className="flex items-center justify-center gap-2 py-4 rounded-2xl bg-red-500 text-white font-black text-lg active:scale-95 transition shadow-lg shadow-red-500/30">
                                <X size={24} strokeWidth={3}/> {t('penalty.bet_miss', 'RATÉ')}
                            </button>
                        </motion.div>
                    ) : (
                        <motion.div key="status"
                                    initial={{opacity: 0}} animate={{opacity: 1}}
                                    className="h-[60px] flex items-center justify-center rounded-2xl bg-white/[0.05] text-white/70 font-semibold text-sm">
                            {locked && isOpen ? (
                                <span className="inline-flex items-center gap-2">
                                    {t('penalty.locked', 'Prono envoyé')} —{' '}
                                    <b className={myPrediction === 'GOAL' ? 'text-action-green' : 'text-red-400'}>
                                        {myPrediction === 'GOAL' ? t('penalty.bet_goal', 'BUT') : t('penalty.bet_miss', 'RATÉ')}
                                    </b>
                                    <span className="text-white/45">· {t('penalty.waiting_kick', 'on attend la frappe…')}</span>
                                </span>
                            ) : (
                                <span>{t('penalty.next_kick', 'Tir suivant…')}</span>
                            )}
                        </motion.div>
                    )}
                </AnimatePresence>
                <p className="text-center text-[11px] text-white/35 mt-3">
                    {t('penalty.hint', 'Plus tu réponds vite et juste, plus tu marques.')}
                </p>
            </div>
        </div>
    );
}

export default PenaltyShootout;
